import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import SpeedDial from '@mui/material/SpeedDial';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTheme } from '@mui/material/styles';

// --- 1. Componente Helper per Evidenziare il Testo ---
const Highlight = ({ text, match }) => {
    if (!match || !text) return text;
    // Divide il testo mantenendo il termine di ricerca (case-insensitive)
    const parts = text.split(new RegExp(`(${match})`, 'gi'));
    
    return parts.map((part, i) => 
        part.toLowerCase() === match.toLowerCase() ? (
            <Box component="span" key={i} sx={{ bgcolor: '#ffeb3b', color: 'black', fontWeight: 'bold' }}>
                {part}
            </Box>
        ) : (
            part
        )
    );
};

const HelpGuideDrawerReport = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const theme = useTheme();

  const toggleDrawer = (newOpen) => () => {
    setOpenDrawer(newOpen);
    if (!newOpen) setSearchTerm("");
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // --- 2. Render Content aggiornato per usare <Highlight> ---
  // Ora accetta 'highlight' (il termine di ricerca) come argomento
  const renderSectionContent = (sectionTitle, highlight) => {
    switch (sectionTitle) {
      case 'introduzione':
        return (
          <Typography variant="body1" paragraph>
            <Highlight text="Benvenuto nella sezione Report! Questa pagina ti permette di analizzare le attività registrate tramite " match={highlight} />
            <strong><Highlight text="filtri avanzati" match={highlight} /></strong>
            <Highlight text=", visualizzare " match={highlight} />
            <strong><Highlight text="statistiche aggregate" match={highlight} /></strong>
            <Highlight text=" o un " match={highlight} />
            <strong><Highlight text="elenco dettagliato" match={highlight} /></strong>
            <Highlight text=". La prima cosa da fare è scegliere il tipo di dati da analizzare tramite i selettori principali." match={highlight} />
          </Typography>
        );
      case 'filtri_controllo':
        return (
          <>
            <Typography variant="body1" paragraph>
              <Highlight text="La parte superiore della pagina è dedicata ai " match={highlight} />
              <strong><Highlight text="filtri" match={highlight} /></strong>
              <Highlight text=". Puoi combinare questi controlli per restringere i dati e generare report specifici." match={highlight} />
            </Typography>
            <ul>
                <li><strong><Highlight text="Selettore di Vista (Attività Club / Lavorative / Formazione)" match={highlight} /></strong>
                    <ul>
                        <li><strong><Highlight text="Funzione:" match={highlight} /></strong> <Highlight text="Questo è il controllo più importante. Determina il " match={highlight} /> <strong><Highlight text="tipo di dati" match={highlight} /></strong> <Highlight text="che verranno analizzati: attività generiche del club, contratti di lavoro o corsi di formazione." match={highlight} /></li>
                        <li><strong><Highlight text="Importante:" match={highlight} /></strong> <Highlight text="La scelta della vista influenza le opzioni disponibili nei filtri 'Tipologia' e 'Seleziona Report'." match={highlight} /></li>
                    </ul>
                </li>
                <li><strong><Highlight text="Campo 'Da' e 'A' (Intervallo di Date)" match={highlight} /></strong>
                    <ul>
                        <li><strong><Highlight text="Funzione:" match={highlight} /></strong> <Highlight text="Specifica un intervallo di date per le attività." match={highlight} /></li> 
                        <li><strong><Highlight text="Importante:" match={highlight} /></strong> <Highlight text="Per i report 'Media Ore' e 'Fasce Orarie', è " match={highlight} /> <strong><Highlight text="obbligatorio" match={highlight} /></strong> <Highlight text="selezionare entrambe le date per generare la griglia temporale." match={highlight} /></li>
                    </ul>
                </li>
                <li><strong><Highlight text="Dropdown 'Tipologia'" match={highlight} /></strong>
                    <ul>
                        <li><strong><Highlight text="Funzione:" match={highlight} /></strong> <Highlight text="Filtra le attività in base alla loro " match={highlight} /> <strong><Highlight text="tipologia specifica" match={highlight} /></strong> <Highlight text="(es. WOD, JS, SE, TE)." match={highlight} /></li>
                    </ul>
                </li>
                <li><strong><Highlight text="Dropdown 'Sede'" match={highlight} /></strong>
                    <ul>
                        <li><strong><Highlight text="Visibilità:" match={highlight} /></strong> <Highlight text="Questo filtro è visibile " match={highlight} /> <strong><Highlight text="solo per gli account Admin" match={highlight} /></strong>.</li>
                        <li><strong><Highlight text="Funzione:" match={highlight} /></strong> <Highlight text="Permette di selezionare una o più sedi per filtrare i dati." match={highlight} /></li>
                    </ul>
                </li>
                <li><strong><Highlight text="Pulsante 'Reset Filtri'" match={highlight} /></strong>
                    <ul>
                        <li><strong><Highlight text="Funzione:" match={highlight} /></strong> <strong><Highlight text="Cancella tutti i filtri" match={highlight} /></strong> <Highlight text="e riporta la pagina alla sua vista predefinita." match={highlight} /></li>
                    </ul>
                </li>
            </ul>
          </>
        );
      case 'seleziona_report':
        return (
          <>
            <Typography variant="body1" paragraph>
              <Highlight text="Il dropdown " match={highlight} /> <strong><Highlight text="Seleziona report" match={highlight} /></strong> <Highlight text="determina il " match={highlight} /> <strong><Highlight text="tipo di statistiche aggregate" match={highlight} /></strong> <Highlight text="da visualizzare. Le opzioni cambiano in base alla vista selezionata." match={highlight} />
            </Typography>
            <ul>
              <li><strong><Highlight text="Opzione 'Nessun report / Dati grezzi':" match={highlight} /></strong> <Highlight text="Nasconde la sezione dei report aggregati e mostra solo la tabella dettagliata in fondo alla pagina." match={highlight} /></li>
            </ul>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}><Highlight text="Per la vista 'Attività Club':" match={highlight} /></Typography>
            <ul>
                <li><strong><Highlight text="Report Iscritti/Attivi:" match={highlight} /></strong> <Highlight text="Mostra il numero di stakeholder unici attivi negli ultimi 30, 60 e 90 giorni." match={highlight} /></li>
                <li><strong><Highlight text="Report Attività:" match={highlight} /></strong> <Highlight text="Fornisce un riepilogo per tipo di attività (WOD, JS, etc.) con il numero di partecipanti unici e il totale ore." match={highlight} /></li>
                <li><strong><Highlight text="Report Media Ore:" match={highlight} /></strong> <Highlight text="Tabella giornaliera con totale e media ore per persona." match={highlight} /></li>
                <li><strong><Highlight text="Report Fasce Orarie:" match={highlight} /></strong> <Highlight text="Una " match={highlight} /> <strong><Highlight text="Heatmap (mappa termica)" match={highlight} /></strong> <Highlight text="che mostra visivamente l'affluenza nelle fasce orarie dalle 07:00 alle 23:00." match={highlight} /></li>
            </ul>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}><Highlight text="Per la vista 'Attività Lavorative':" match={highlight} /></Typography>
            <ul>
                <li><strong><Highlight text="Contratti per Azienda/Anno:" match={highlight} /></strong> <Highlight text="Tabella che incrocia aziende e anni, mostrando il numero di contratti." match={highlight} /></li>
                <li><strong><Highlight text="Nuovi Contratti per Anno:" match={highlight} /></strong> <Highlight text="Tabella che mostra il trend dei nuovi contratti attivati annualmente." match={highlight} /></li>
                <li><strong><Highlight text="Contratti per Tipo / Esito:" match={highlight} /></strong> <Highlight text="Statistiche sui contratti nel periodo filtrato, raggruppati per tipologia o per esito." match={highlight} /></li>
            </ul>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}><Highlight text="Per la vista 'Formazione':" match={highlight} /></Typography>
            <ul>
                <li><strong><Highlight text="Statistiche:" match={highlight} /></strong> <Highlight text="Fornirà un riepilogo sui corsi e sui certificati ottenuti." match={highlight} /></li>
            </ul>
          </>
        );
      case 'dettaglio_report':
        return (
            <>
                <Typography variant="body1" paragraph>
                    <Highlight text="Questa sezione, che appare solo quando selezioni un report, mostra i dati in forma aggregata. Ecco i dettagli per i report più complessi." match={highlight} />
                </Typography>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}><Highlight text="Report per 'Attività Club'" match={highlight} /></Typography>
                <ul>
                    <li><strong><Highlight text="Report Iscritti/Attivi:" match={highlight} /></strong> <Highlight text="Mostra tre riquadri con il numero di persone uniche attive negli ultimi 30/60/90 giorni." match={highlight} /></li>
                    <li><strong><Highlight text="Report Attività:" match={highlight} /></strong> <Highlight text="Riepilogo per tipo (WOD, JS...) con partecipanti unici e monte ore totale." match={highlight} /></li>
                    <li><strong><Highlight text="Report Media Ore:" match={highlight} /></strong> <Highlight text="Tabella che elenca giorno per giorno il totale delle ore erogate e la media ore per utente. Include una riga finale di riepilogo totale." match={highlight} /></li>
                    <li><strong><Highlight text="Report Fasce Orarie (Heatmap):" match={highlight} /></strong>
                        <ul>
                            <li><Highlight text="Mostra una griglia temporale dalle " match={highlight} /> <strong><Highlight text="07:00 alle 23:00" match={highlight} /></strong>.</li>
                            <li><strong><Highlight text="Conteggio Unico:" match={highlight} /></strong> <Highlight text="I numeri indicano quante " match={highlight} /> <em><Highlight text="persone fisiche diverse" match={highlight} /></em> <Highlight text="erano presenti in quella fascia oraria (non il numero di azioni)." match={highlight} /></li>
                            <li><strong><Highlight text="Colori (Heatmap):" match={highlight} /></strong> <Highlight text="La tabella usa una scala di verdi. " match={highlight} /> <strong><Highlight text="Verde scuro" match={highlight} /></strong> <Highlight text="indica un'alta affluenza (sopra l'80% del massimo registrato), verde chiaro un'affluenza minore. Le celle vuote indicano 0 presenze." match={highlight} /></li>
                            <li><strong><Highlight text="Dettaglio per Sede:" match={highlight} /></strong> <Highlight text="Passando il mouse sopra un numero, apparirà un " match={highlight} /> <strong><Highlight text="Tooltip" match={highlight} /></strong> <Highlight text="che mostra la divisione delle presenze per Sede (es. " match={highlight} /> <em><Highlight text="Milano: 5, Roma: 3" match={highlight} /></em>).</li>
                        </ul>
                    </li>
                </ul>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}><Highlight text="Report per 'Attività Lavorative'" match={highlight} /></Typography>
                <ul>
                    <li><strong><Highlight text="Contratti per Azienda/Anno:" match={highlight} /></strong> <Highlight text="Matrice Aziende x Anni." match={highlight} /></li>
                    <li><strong><Highlight text="Nuovi Contratti per Anno:" match={highlight} /></strong> <Highlight text="Trend annuale nuovi contratti." match={highlight} /></li>
                    <li><strong><Highlight text="Contratti per Tipo / Esito:" match={highlight} /></strong> <Highlight text="Conteggi raggruppati per tipologia contrattuale o esito (positivo/negativo) nel periodo selezionato." match={highlight} /></li>
                </ul>
            </>
        );
      case 'tabella_dati':
        return (
          <>
            <Typography variant="body1" paragraph>
              <Highlight text="Nella parte inferiore della pagina, troverai sempre una " match={highlight} /> <strong><Highlight text="tabella dettagliata (DataGrid)" match={highlight} /></strong> <Highlight text="che elenca tutte le " match={highlight} /> <strong><Highlight text="attività risultanti dai filtri" match={highlight} /></strong>.
            </Typography>
            <ul>
              <li><strong><Highlight text="Colonne Dinamiche:" match={highlight} /></strong> <Highlight text="Le colonne della tabella si adattano automaticamente alla vista selezionata per mostrare le informazioni più pertinenti (es. ore per il Club, mansione per il Lavoro, ente per la Formazione)." match={highlight} /></li>
              <li><strong><Highlight text="Indicatore di Caricamento:" match={highlight} /></strong> <Highlight text="Mentre i dati vengono caricati dal server, la tabella mostrerà un indicatore di caricamento." match={highlight} /></li>
              <li><strong><Highlight text="Esportazione:" match={highlight} /></strong> <Highlight text="Puoi " match={highlight} /> <strong><Highlight text="esportare i dati visibili" match={highlight} /></strong> <Highlight text="nella tabella in formato Excel usando il pulsante con l'icona della freccia in alto a destra." match={highlight} /></li>
              <li><strong><Highlight text="Dettaglio Azione:" match={highlight} /></strong> <Highlight text="Facendo " match={highlight} /> <strong><Highlight text="doppio clic" match={highlight} /></strong> <Highlight text="su una riga, verrai reindirizzato alla pagina di modifica di quell'azione." match={highlight} /></li>
            </ul>
          </>
        );
      case 'azioni_tabella':
        return (
          <>
            <Typography variant="body1" paragraph>
              <Highlight text="La " match={highlight} /> <strong><Highlight text="tabella dettagliata" match={highlight} /></strong> <Highlight text="offre diverse " match={highlight} /> <strong><Highlight text="funzionalità di interazione" match={highlight} /></strong> <Highlight text="per analizzare i dati." match={highlight} />
            </Typography>
            <ul>
              <li><strong><Highlight text="Ordinamento:" match={highlight} /></strong> <Highlight text="Clicca sull'" match={highlight} /> <strong><Highlight text="intestazione di una colonna" match={highlight} /></strong> <Highlight text="per ordinare i dati in base a quella colonna." match={highlight} /></li>
              <li><strong><Highlight text="Filtri per Colonna:" match={highlight} /></strong> <Highlight text="Clicca sull'icona con i tre puntini su un'intestazione di colonna e seleziona 'Filtro' per aprire un pannello di filtri avanzati per quella colonna." match={highlight} /></li>
              <li><strong><Highlight text="Gestione Colonne:" match={highlight} /></strong> <Highlight text="Usa l'apposito pulsante nella toolbar della tabella per nascondere o mostrare le colonne che ti interessano." match={highlight} /></li>
            </ul>
          </>
        );
      case 'problemi_comuni':
        return (
          <>
            <ul>
              <li><strong><Highlight text="Problema:" match={highlight} /></strong> <Highlight text="La tabella Fasce Orarie o Media Ore è vuota." match={highlight} />
                <br />
                <strong><Highlight text="Soluzione:" match={highlight} /></strong> <Highlight text="Assicurati di aver selezionato " match={highlight} /> <strong><Highlight text="sia la Data Inizio che la Data Fine" match={highlight} /></strong> <Highlight text="nei filtri in alto. Questi report necessitano di un intervallo temporale definito per generare le colonne." match={highlight} /></li>
              <li><strong><Highlight text="Problema:" match={highlight} /></strong> <Highlight text="La tabella principale non mostra dati." match={highlight} />
                <br />
                <strong><Highlight text="Soluzione:" match={highlight} /></strong> <Highlight text="Clicca su " match={highlight} /> <strong><Highlight text="Reset Filtri" match={highlight} /></strong> <Highlight text=". Se ancora non vedi dati, prova ad ampliare l'intervallo di date." match={highlight} /></li>
              <li><strong><Highlight text="Problema:" match={highlight} /></strong> <Highlight text="Il report che cerco non è nell'elenco." match={highlight} />
                <br />
                <strong><Highlight text="Soluzione:" match={highlight} /></strong> <Highlight text="Controlla la " match={highlight} /> <strong><Highlight text="vista selezionata" match={highlight} /></strong> <Highlight text="(Attività Club / Lavorative / Formazione). Ogni vista ha report diversi." match={highlight} /></li>
            </ul>
          </>
        );
      default:
        return null;
    }
  };

  // Dati organizzati con 'searchText' per la ricerca (testo puro)
  // Il 'content' viene generato dinamicamente passando 'searchTerm'
  const accordionSections = [
    { 
        id: 'introduzione', 
        title: 'Benvenuto nella Guida!', 
        content: (term) => renderSectionContent('introduzione', term),
        searchText: "introduzione benvenuto report filtri avanzati statistiche aggregate elenco dettagliato tipo di dati analizzare"
    },
    { 
        id: 'filtri_controllo', 
        title: '1. I Filtri Principali', 
        content: (term) => renderSectionContent('filtri_controllo', term),
        searchText: "filtri principali selettore vista attività club lavorative formazione campo da a data inizio fine intervallo tipologia sede reset filtri"
    },
    { 
        id: 'seleziona_report', 
        title: '2. Il Dropdown "Seleziona Report"', 
        content: (term) => renderSectionContent('seleziona_report', term),
        searchText: "seleziona report dropdown menu statistiche aggregate nessun report iscritti attivi attività media ore fasce orarie contratti azienda anno esito formazione"
    },
    { 
        id: 'dettaglio_report', 
        title: '3. Dettaglio dei Report Aggregati', 
        content: (term) => renderSectionContent('dettaglio_report', term),
        searchText: "dettaglio report aggregati club lavorative persone uniche partecipanti monte ore heatmap mappa termica fasce orarie verde tooltip sede milano roma contratti"
    },
    { 
        id: 'tabella_dati', 
        title: '4. La Tabella dei Dati Dettagliati', 
        content: (term) => renderSectionContent('tabella_dati', term),
        searchText: "tabella dati dettagliati datagrid colonne dinamiche caricamento export excel doppio clic modifica azione"
    },
    { 
        id: 'azioni_tabella', 
        title: '5. Interagire con la Tabella', 
        content: (term) => renderSectionContent('azioni_tabella', term),
        searchText: "interagire tabella ordinamento filtri colonna gestione colonne nascondere mostrare"
    },
    { 
        id: 'problemi_comuni', 
        title: '6. Problemi Comuni', 
        content: (term) => renderSectionContent('problemi_comuni', term),
        searchText: "problemi comuni risoluzione tabella vuota data inizio fine reset filtri vista selezionata"
    },
  ];

  // Logica di filtraggio
  const filteredSections = useMemo(() => {
    if (!searchTerm) return accordionSections;
    const lowerTerm = searchTerm.toLowerCase();
    return accordionSections.filter(section => 
        section.title.toLowerCase().includes(lowerTerm) || 
        section.searchText.toLowerCase().includes(lowerTerm)
    );
  }, [searchTerm, accordionSections]);

  return (
    <>
      <SpeedDial
        ariaLabel="Guida alla pagina Report"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<HelpOutlineIcon />}
        onClick={toggleDrawer(true)}
      />

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: { xs: '90%', sm: 450, md: 550 },
            zIndex: theme.zIndex.drawer + 2,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column', 
            p: 2,
            borderBottom: '1px solid lightgrey',
            backgroundColor: 'white',
            position: 'sticky', 
            top: 0,
            zIndex: 10
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 2 }}>
            <Typography variant="h6" component="div">
                Guida Report
            </Typography>
            <IconButton onClick={toggleDrawer(false)}>
                <CloseIcon />
            </IconButton>
          </Box>

          {/* Campo di Ricerca */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Cerca nella guida..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Box sx={{ p: 2, overflowY: 'auto' }}>
          {filteredSections.length > 0 ? (
            filteredSections.map((section) => (
              <Accordion
                key={section.id}
                // Espande automaticamente se c'è una ricerca attiva
                expanded={searchTerm ? true : expandedAccordion === section.id}
                onChange={handleAccordionChange(section.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${section.id}-content`}
                  id={`${section.id}-header`}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {/* Applica Highlight anche al titolo */}
                    <Highlight text={section.title} match={searchTerm} />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Passa il searchTerm per evidenziare il contenuto */}
                  {section.content(searchTerm)}
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
             <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                <Typography>Nessun risultato trovato per "{searchTerm}"</Typography>
             </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default HelpGuideDrawerReport;